import React, { useState } from 'react';
import { CreateFilterGroup } from './create-filter';
import { Modal } from 'components/modal';
import { Button } from 'components/button';
import { gql } from '@apollo/client';
import {
  ContentFilterGroupsScreenQuery,
  CreateContentFilterGroupMutation,
  CreateContentFilterGroupMutationVariables,
} from 'graphql/types';
import { useQuery, useMutation } from '@apollo/client';
import { FilterGroupsTable } from 'components/content/filter-groups-table';
import { useNotifications } from 'notifications';
import { Loading } from 'components/loading';

const ContentFilterGroupsPage = () => {
  const contentFilterGroupsQuery = gql`
    query ContentFilterGroupsScreen {
      contentTags {
        id
        name
      }
      contentFilterGroups {
        id
        title
        contentTag {
          id
          name
        }
      }
    }
  `;
  const { data, loading } = useQuery<ContentFilterGroupsScreenQuery>(
    contentFilterGroupsQuery,
  );

  const [createFilterGroup] = useMutation<
    CreateContentFilterGroupMutation,
    CreateContentFilterGroupMutationVariables
  >(
    gql`
      mutation CreateContentFilterGroup(
        $input: CreateContentFilterGroupInput!
      ) {
        createContentFilterGroup(input: $input) {
          filterGroup {
            id
            title
            contentTag {
              id
              name
            }
          }
        }
      }
    `,
    {
      update(cache, { data }) {
        if (!data?.createContentFilterGroup?.filterGroup) return;

        const existingFilterGroups =
          cache.readQuery<ContentFilterGroupsScreenQuery>({
            query: contentFilterGroupsQuery,
          });

        if (!existingFilterGroups?.contentFilterGroups) return;

        cache.writeQuery<ContentFilterGroupsScreenQuery>({
          query: contentFilterGroupsQuery,
          data: {
            contentFilterGroups: [
              data.createContentFilterGroup.filterGroup,
              ...existingFilterGroups.contentFilterGroups,
            ],
          },
        });
      },
      onCompleted: () => {
        showNotification({
          message: 'Filter group created successfully',
          type: 'success',
        });
        setShowModal(false);
      },
      onError: (error) => {
        showNotification({
          message: error.message,
          type: 'error',
        });
      },
    },
  );

  const [showModal, setShowModal] = useState(false);
  const showNotification = useNotifications();

  const contentTags = data?.contentTags;
  const filterGroups = data?.contentFilterGroups;

  const onCreateFilterGroup = ({
    title,
    contentCategoryId,
    filterItems,
  }: {
    title: string;
    contentCategoryId: string;
    filterItems: string[];
  }) => {
    createFilterGroup({
      variables: {
        input: {
          title,
          contentCategoryId,
          filterItems,
        },
      },
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-8">
        <div>
          <Button
            fullWidth
            variant="outline"
            onClick={() => setShowModal(true)}
          >
            Add Filter Group
          </Button>
        </div>
        <Modal
          show={showModal}
          width="max-w-screen-md"
          isAutoOverflow={false}
          onClose={() => setShowModal(false)}
        >
          <div className="bg-white rounded p-5">
            <CreateFilterGroup
              onClose={() => setShowModal(false)}
              contentTags={data?.contentTags}
              onCreateFilterGroup={onCreateFilterGroup}
              loading={loading}
            />
          </div>
        </Modal>
      </div>
      {loading ? (
        <Loading />
      ) : (
        <FilterGroupsTable
          contentTags={contentTags}
          filterGroups={filterGroups}
        />
      )}
    </div>
  );
};

export default ContentFilterGroupsPage;
